import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Sparkles, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * One-tap "Already Logged" chip for the Continuity Morning Nudge.
 *
 * Silently no-ops when the current user is not attached to this
 * project (the /status endpoint returns 403; we hide instead of
 * flashing an error). Once acked, the chip persists in the acked
 * state for the rest of the shooting day.
 */
export default function ContinuityNudgeChip({ projectId, testid = "cn-chip" }) {
  const [state, setState] = useState({ loaded: false, acked: false, ack: null,
                                        visible: true, busy: false });

  const load = () =>
    api.get(`/continuity-nudge/status`, { params: { project_id: projectId } })
       .then(({ data }) => setState((s) => ({
         ...s, loaded: true, acked: !!data.acked, ack: data.ack, visible: true,
       })))
       .catch((err) => {
         // 403 = user has no relationship with this project — just hide.
         if (err?.response?.status === 403 || err?.response?.status === 404) {
           setState({ loaded: true, acked: false, ack: null, visible: false, busy: false });
         } else {
           setState((s) => ({ ...s, loaded: true, visible: false }));
         }
       });

  useEffect(() => { if (projectId) load(); }, [projectId]);

  if (!state.loaded || !state.visible) return null;

  if (state.acked) {
    const sinceIso = state.ack?.created_at || state.ack?.last_seen_at;
    const since = sinceIso ? _relTime(sinceIso) : "";
    return (
      <span
        className="inline-flex items-center gap-1.5 rounded-full border border-emerald-400/35 bg-emerald-500/10 text-emerald-200 text-[10px] tracking-[0.22em] uppercase px-3 py-1.5"
        data-testid={`${testid}-acked`}>
        <Check size={11} /> Logged {since && <span className="text-white/50 normal-case tracking-normal font-normal ml-1">· {since}</span>}
      </span>
    );
  }

  return (
    <button
      type="button"
      disabled={state.busy}
      onClick={async () => {
        setState((s) => ({ ...s, busy: true }));
        try {
          const { data } = await api.post(`/continuity-nudge/ack`,
            { project_id: projectId });
          setState({ loaded: true, acked: true, ack: data.ack,
                     visible: true, busy: false });
          toast.success("Nice — reminder silenced for today");
        } catch (err) {
          setState((s) => ({ ...s, busy: false }));
          toast.error(formatApiErrorDetail(err.response?.data?.detail)
                       || "Could not acknowledge");
        }
      }}
      className="inline-flex items-center gap-1.5 rounded-full border border-amber-400/30 bg-amber-400/10 hover:bg-amber-400/20 hover:border-amber-300/60 text-amber-200 hover:text-amber-100 text-[10px] tracking-[0.22em] uppercase px-3 py-1.5 disabled:opacity-50 transition-colors"
      data-testid={`${testid}-btn`}>
      {state.busy ? <Loader2 size={11} className="animate-spin" />
                  : <Sparkles size={11} />}
      Already Logged
    </button>
  );
}


function _relTime(iso) {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "";
  const s = Math.floor((Date.now() - t) / 1000);
  if (s < 60)   return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}
